import { useTranslation } from "react-i18next";
import { Address } from "../../../models/Address";
import { useEffect, useMemo, useState } from "react";
import Button from "../../Button";
import { BookOpenIcon, PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import FilteredTable from "../../FilteredTable";
import { ClientAddressModal } from "./ClientAddressModal";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../state/store";
import { fetchAddressesByClientId, needToLoadClientAddress, setClientMainAddress } from "../../../state/addresses/actions";
import { Client } from "../../../models/Client";
import { LoadingMask } from "../../LoadingMask";
import { Toast } from "../../Toast";
import { toast, ToastContainer } from "react-toastify";
import { DeleteClientAddressModal } from "./DeleteClientAddressModal";
import { SetClientMainAddressModal } from "./SetClientMainAddressModal";
import { StarIcon } from "@heroicons/react/24/outline";


interface Props {
    isNewClient:boolean
    addresses: Address[];
    canEditClient: boolean
    addAdress: () => void;
    removeAddress: (index: number) => void;
    updateAddress: (index: number, address: Address) => void;
    setMainAddress: (index: number, address: Address) => void;
}

export const ClientAddressTable: React.FC<Props> = ({
  isNewClient,
  addresses,
  canEditClient,
  addAdress,
  removeAddress,
  updateAddress,
  setMainAddress
}) => {
    const { t } = useTranslation();

    const columns = useMemo(() => {
        const showColumns = [
            {
                Header: t("STREET_ADDRESS"),
                Cell: (row: any) => row.row.original.address_line_1 ? `${row.row.original.address_line_1}` : "-"
            },
            {
                Header: t("STREET_ADDRESS2"),
                Cell: (row: any) => row.row.original.address_line_2 ? `${row.row.original.address_line_2}` : "-",
            },
            {
                Header: t("CITY"),
                Cell: (row: any) => row.row.original.city ? `${row.row.original.city}` : "-"
            },
            {
                Header: t("STATE"),
                Cell: (row: any) => row.row.original.state ? `${row.row.original.state}` : "-"
            },
            {
                Header: t("ZIP_CODE"),
                Cell: (row: any) => row.row.original.zip_code ? `${row.row.original.zip_code}` : "-"
            }
        ]

        const actions = {
            id: "actions",
            Header: "",
            Cell: (row: any) => {
                return (
                    <div className="flex flex-row justify-center divide-x">
                        <Button
                            isTerciary
                            iconOnly
                            tooltip={t("UPDATE_ADDRESS")}
                            onClick={() => updateAddress(row.row.index, row.row.original)}
                            classNames="mr-2"
                        >
                            <PencilIcon className="h-5 aspect-square text-blue" />
                        </Button>
                        <Button
                            isTerciary
                            iconOnly
                            tooltip={t("DELETE_ADDRESS")}
                            onClick={() => removeAddress(row.row.index) }
                            classNames="mr-2"
                            isDisabled={(!isNewClient && addresses.length === 1)}
                        >
                            <TrashIcon className="h-5 aspect-square text-red-600" />
                        </Button>
                        <Button
                            isTerciary
                            iconOnly
                            isDisabled={isNewClient || row.row.original.is_main_address === "1"}
                            tooltip={t("SET_AS_MAIN_ADDRESS")}
                            onClick={() => setMainAddress(row.row.index, row.row.original)}
                            classNames="mr-2">
                                <StarIcon className="h-5 aspect-square text-gold" />
                        </Button>
                    </div>
                );
            },
        }
        
        const backupActions = {
            id: "actions",
            Header: "",
            Cell: (row: any) => {
                return (
                    <div className="flex flex-row justify-center divide-x">
                        <Button
                            isTerciary
                            iconOnly
                            tooltip={t("ADDRESS_INFORMATION")}
                            onClick={() => updateAddress(row.row.index, row.row.original)}
                            classNames="mr-2"
                        >
                            <BookOpenIcon className="h-5 aspect-square text-blue" />
                        </Button>
                    </div>
                );
            },
        }

        if(!canEditClient) {
            return [...showColumns, backupActions]
        }

        return [...showColumns, actions]
    },[removeAddress, t, updateAddress]);

    return (
        <FilteredTable
            columns={columns}
            data={addresses}
            hasSearchBar={false}
            secondaryActionButton={
                canEditClient
                ? <Button onClick={addAdress}>
                        <span className="flex items-center font-semibold pr-3">
                            <div className="w-8 p-1 aspect-square mr-2">
                                <PlusIcon />
                            </div>
                            {t("ADD_ADDRESS")}
                        </span>
                    </Button>
                : <div></div>
            }
        />
    )
}